import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface TrashedProject {
  id: string;
  slug: string;
  githubRepo: string;
  deletedAt: string;
}

const RETENTION_DAYS = 30;

/**
 * T137 — soft-delete recovery within the 30-day window. After 30d the
 * gc-soft-deleted cron hard-deletes the row + artifacts; no recovery
 * after that.
 */
export function Trash() {
  const { org } = useParams<{ org: string }>();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["trash", org],
    queryFn: () => api.get<{ projects: TrashedProject[] }>(`/orgs/${org}/trash`).then((r) => r.projects),
  });

  const restore = useMutation({
    mutationFn: (slug: string) => api.post(`/orgs/${org}/projects/${slug}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trash", org] });
      qc.invalidateQueries({ queryKey: ["projects", org] });
    },
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Trash</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Soft-deleted projects are kept for {RETENTION_DAYS} days then hard-deleted (artifacts + DB).
        </p>
      </div>
      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" /> Trashed projects
          </h3>
          <span className="text-2xs text-subtle-foreground">{q.data?.length ?? 0}</span>
        </div>
        {q.isLoading && <Empty>Loading…</Empty>}
        {!q.isLoading && (q.data?.length ?? 0) === 0 && <Empty>Trash is empty.</Empty>}
        {q.data?.map((p) => {
          const days = Math.max(0, RETENTION_DAYS - Math.floor((Date.now() - new Date(p.deletedAt).getTime()) / 86400000));
          return (
            <div key={p.id} className="of-row gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-semibold truncate">{p.slug}</div>
                <div className="text-2xs text-subtle-foreground font-mono truncate">{p.githubRepo}</div>
                <div className="text-2xs text-muted-foreground mt-0.5">
                  Deleted {new Date(p.deletedAt).toLocaleString()} · purges in {days}d
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => restore.mutate(p.slug)}
                disabled={restore.isPending}
              >
                {restore.isPending && restore.variables === p.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                Restore
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}
